import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { deriveNeighbourhoodFromAddress } from '../mocks/mockAvailability';
import type { BillingInterval, PartnerBusiness, WizardBasicsSubmit, WizardStep } from '../types';
import { getListingValidUntilFromActivation } from '../utils/billingPlans';
import { normalizeStoredClasses } from '../utils/businessClasses';
import { DEFAULT_WEEK_HOURS } from '../utils/defaultHours';
import { addMonths, BILLING_INTERVAL_MONTHS } from '../utils/listingPeriod';
import { normalizeBusinessSubscription } from '../utils/subscription';

const STORAGE_KEY = 'chisme_partner_business_v2';

type CreateDraftInput = WizardBasicsSubmit;

type PartnerBusinessContextValue = {
  business: PartnerBusiness | null;
  isLoading: boolean;
  createDraft: (input: CreateDraftInput, wizardStep?: WizardStep) => PartnerBusiness;
  updateDraft: (patch: Partial<PartnerBusiness>) => void;
  setWizardStep: (step: WizardStep) => void;
  activateBusiness: (billingInterval?: BillingInterval) => void;
  cancelSubscription: () => void;
  clearBusiness: () => void;
};

const PartnerBusinessContext = createContext<PartnerBusinessContextValue | null>(null);

function readStoredBusiness(): PartnerBusiness | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PartnerBusiness;
    const normalized = normalizeBusinessSubscription({
      ...parsed,
      classes: normalizeStoredClasses(parsed.classes),
      hoursEnabled: parsed.hoursEnabled ?? true,
      photoPreviewUrls: parsed.photoPreviewUrls ?? parsed.importedPhotoUrls,
      listingValidUntil:
        parsed.listingValidUntil ??
        (parsed.activatedAt
          ? addMonths(
              new Date(parsed.activatedAt),
              BILLING_INTERVAL_MONTHS[parsed.billingInterval ?? 'monthly'],
            ).toISOString()
          : undefined),
      billingInterval: parsed.billingInterval ?? 'monthly',
      subscriptionStatus: parsed.subscriptionStatus ?? 'active',
    });

    if (normalized.status !== parsed.status) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    }

    return normalized;
  } catch {
    return null;
  }
}

function createId(): string {
  return `biz_${Date.now().toString(36)}`;
}

export const PartnerBusinessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [business, setBusiness] = useState<PartnerBusiness | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setBusiness(readStoredBusiness());
    setIsLoading(false);
  }, []);

  const persistBusiness = useCallback((next: PartnerBusiness | null) => {
    setBusiness(next);
    if (next) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const createDraft = useCallback(
    (input: CreateDraftInput, wizardStep: WizardStep = 1): PartnerBusiness => {
      const now = new Date().toISOString();
      const neighbourhood = deriveNeighbourhoodFromAddress(input.address, input.cityId);

      const draft: PartnerBusiness = {
        id: createId(),
        status: 'draft',
        wizardStep,
        name: input.name,
        cityId: input.cityId,
        category: input.category,
        address: input.address,
        neighbourhood: neighbourhood.label,
        description: input.description ?? '',
        website: input.website ?? '',
        instagram: '',
        hours: input.hours ?? DEFAULT_WEEK_HOURS,
        hoursEnabled: true,
        classes: [],
        photoCount: input.importedPhotoUrls?.length ?? 0,
        googlePlaceId: input.googlePlaceId,
        googleMapsUrl: input.googleMapsUrl,
        importedPhotoUrls: input.importedPhotoUrls,
        photoPreviewUrls: input.importedPhotoUrls,
        createdAt: now,
        updatedAt: now,
      };

      persistBusiness(draft);
      return draft;
    },
    [persistBusiness],
  );

  const updateDraft = useCallback(
    (patch: Partial<PartnerBusiness>) => {
      setBusiness((current) => {
        if (!current) return null;

        const nextAddress = patch.address ?? current.address;
        const nextCityId = patch.cityId ?? current.cityId;
        const neighbourhood = deriveNeighbourhoodFromAddress(nextAddress, nextCityId);

        const next: PartnerBusiness = {
          ...current,
          ...patch,
          neighbourhood: neighbourhood.label,
          classes: patch.classes ?? current.classes ?? [],
          updatedAt: new Date().toISOString(),
        };

        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        return next;
      });
    },
    [],
  );

  const setWizardStep = useCallback(
    (step: WizardStep) => {
      updateDraft({ wizardStep: step });
    },
    [updateDraft],
  );

  const activateBusiness = useCallback((billingInterval: BillingInterval = 'monthly') => {
    setBusiness((current) => {
      if (!current) return null;

      const activatedAt = new Date();
      const activatedAtIso = activatedAt.toISOString();
      const next: PartnerBusiness = {
        ...current,
        status: 'active',
        wizardStep: 4,
        activatedAt: activatedAtIso,
        billingInterval,
        subscriptionStatus: 'active',
        subscriptionCancelledAt: undefined,
        listingValidUntil: getListingValidUntilFromActivation(activatedAt, billingInterval).toISOString(),
        updatedAt: activatedAtIso,
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const cancelSubscription = useCallback(() => {
    setBusiness((current) => {
      if (!current || current.status !== 'active') return current;

      const next: PartnerBusiness = {
        ...current,
        subscriptionStatus: 'cancelled',
        subscriptionCancelledAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const clearBusiness = useCallback(() => {
    persistBusiness(null);
  }, [persistBusiness]);

  const value = useMemo(
    () => ({
      business,
      isLoading,
      createDraft,
      updateDraft,
      setWizardStep,
      activateBusiness,
      cancelSubscription,
      clearBusiness,
    }),
    [business, isLoading, createDraft, updateDraft, setWizardStep, activateBusiness, cancelSubscription, clearBusiness],
  );

  return <PartnerBusinessContext.Provider value={value}>{children}</PartnerBusinessContext.Provider>;
};

export function usePartnerBusiness(): PartnerBusinessContextValue {
  const ctx = useContext(PartnerBusinessContext);
  if (!ctx) {
    throw new Error('usePartnerBusiness must be used within PartnerBusinessProvider');
  }
  return ctx;
}
